import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'next-i18next';

import CardPartner from '../../components/CardPartner';

import styles from './ListCardPartner.module.scss';
import RegionsMap from '../RegionsMap';
import classNames from 'classnames';

const ListCardPartner = ({ isActiveTab = false, items = [] }) => {
    const [activeRegion, setActiveRegion] = useState(null);

    const { t } = useTranslation();

    const regionRef = useRef();

    const regionOrder = ['Northern Sweden', 'Middle Sweden', 'Southern Sweden'];
    // Group items by region and county
    const groupedItems = items.reduce(
        (acc, item) => {
            const { region, county } = item;

            const regionKey = region?.title || county?.region || null;

            if (regionKey) {
                if (!acc.regions[regionKey]) {
                    acc.regions[regionKey] = {
                        slug: region?.slug || regionKey,
                        header: region?.header || regionKey,
                        preamble: region?.preamble || '',
                        counties: {},
                        noCounty: [],
                    };
                }

                if (county.length > 0) {
                    county.forEach((cd) => {
                        if (!acc.regions[regionKey].counties[cd.county]) {
                            acc.regions[regionKey].counties[cd.county] = [];
                        }
                        acc.regions[regionKey].counties[cd.county].push(item);
                    });
                } else {
                    acc.regions[regionKey].noCounty.push(item);
                }
            } else {
                acc.noRegion.push(item);
            }

            return acc;
        },
        { regions: {}, noRegion: [] }
    );

    useEffect(() => {
        const handleHashChange = () => {
            const slug = window.location.hash.replace('#', '');
            if (slug && isActiveTab) {
                const targetElement = document.getElementById(slug);
                if (targetElement) {
                    targetElement.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                    });
                }
            }
        };
        window.addEventListener('hashchange', handleHashChange);
        handleHashChange();
        return () => {
            window.removeEventListener('hashchange', handleHashChange);
        };
    }, [isActiveTab]);

    const sortedRegionKeys = Object.keys(groupedItems.regions).sort(
        (a, b) => regionOrder.indexOf(a) - regionOrder.indexOf(b)
    );

    const regionButtons = sortedRegionKeys.map((region) => ({
        text: groupedItems.regions[region].header,
        id: groupedItems.regions[region].slug,
    }));

    const sortedRegions = sortedRegionKeys.map((region) => [
        region,
        groupedItems.regions[region],
    ]);

    useEffect(() => {
        if (
            typeof window === 'undefined' ||
            typeof IntersectionObserver === 'undefined' ||
            !isActiveTab
        ) {
            return;
        }

        const observerOptions = {
            threshold: Array.from({ length: 100 }, (_, i) => i / 100), // Track visibility in 1% increments
        };
        const observer = new IntersectionObserver((entries) => {
            // Change active region depending on how much of the viewport the element fills
            let maxVisibleRatio = 0;
            let slugWithMaxVisibility = null;
            const visibleRatioInViewport = 0.5;
            const viewportHeight =
                window.innerHeight || document.documentElement.clientHeight;

            entries.forEach((entry) => {
                // Calculate how much of the viewport the element occupies
                const visibleHeight = Math.min(
                    viewportHeight,
                    entry.intersectionRect.height
                );
                const elementVisibleRatio = visibleHeight / viewportHeight;

                if (
                    elementVisibleRatio >= visibleRatioInViewport &&
                    elementVisibleRatio > maxVisibleRatio
                ) {
                    maxVisibleRatio = elementVisibleRatio;
                    slugWithMaxVisibility = entry.target.getAttribute('id');
                }
            });

            if (slugWithMaxVisibility) {
                window.history.replaceState(
                    null,
                    '',
                    `#${slugWithMaxVisibility}`
                );
                setActiveRegion(slugWithMaxVisibility);
            }
        }, observerOptions);

        const regionElements = sortedRegions.map(([, regionData]) =>
            document.getElementById(regionData.slug)
        );

        regionElements.forEach((element) => {
            if (element) observer.observe(element);
        });

        return () => observer.disconnect();
    }, [sortedRegions]);

    return (
        <div className={styles['ListCardPartner']} ref={regionRef}>
            <div className={styles['ListCardPartner__Container']}>
                <nav
                    className={`sr-only ${styles['ListCardPartner__Nav']}`}
                    aria-label="Region navigation"
                >
                    {regionButtons.map(({ text, id }, index) => (
                        <a key={`region-link-${index}`} href={`#${id}`}>
                            {t('listCardPartner.goToRegion', { region: text })}
                        </a>
                    ))}
                </nav>
                {/* Map with region buttons */}
                <div className={styles['ListCardPartner__Map']}>
                    {groupedItems && (
                        <RegionsMap
                            setActiveRegion={setActiveRegion}
                            activeRegion={activeRegion}
                        />
                    )}
                </div>

                {/* List of regions and counties */}
                <div className={styles['ListCardPartner__List']}>
                    {sortedRegions.map(([regionKey, regionData]) => (
                        <div
                            key={regionKey}
                            id={regionData.slug}
                            className={styles['ListCardPartner__Region']}
                        >
                            <h3 className={styles['ListCardPartner__Title']}>
                                {regionData.header}
                            </h3>
                            <p className={styles['ListCardPartner__Preamble']}>
                                {regionData.preamble}
                            </p>

                            {/* Counties within the region */}
                            {Object.entries(regionData?.counties).map(
                                ([county, countyItems]) => (
                                    <div
                                        key={county}
                                        className={
                                            styles['ListCardPartner__County']
                                        }
                                    >
                                        <h4
                                            className={
                                                styles[
                                                    'ListCardPartner__Subtitle'
                                                ]
                                            }
                                        >
                                            {county}
                                        </h4>
                                        <ul
                                            className={
                                                styles[
                                                    'ListCardPartner__CardList'
                                                ]
                                            }
                                        >
                                            {countyItems.map((item) => (
                                                <li
                                                    key={item.id}
                                                    className={
                                                        styles[
                                                            'ListCardPartner__CardItem'
                                                        ]
                                                    }
                                                >
                                                    <CardPartner {...item} />
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )
                            )}

                            {/* Items without county */}
                            {regionData?.noCounty.length > 0 && (
                                <div
                                    className={
                                        styles['ListCardPartner__NoCounty']
                                    }
                                >
                                    <h4 className="sr-only">
                                        {t('listCardPartner.otherCounties')}
                                    </h4>
                                    <ul
                                        className={
                                            styles['ListCardPartner__CardList']
                                        }
                                    >
                                        {regionData?.noCounty.map(
                                            (item, index) => (
                                                <li
                                                    key={item.id}
                                                    className={
                                                        styles[
                                                            'ListCardPartner__CountyItem'
                                                        ]
                                                    }
                                                >
                                                    <CardPartner {...item} />
                                                </li>
                                            )
                                        )}
                                    </ul>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

ListCardPartner.propTypes = {
    items: PropTypes.array.isRequired,
    activeSortPath: PropTypes.string,
};

export default ListCardPartner;
